import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";

export function ReturnToParentSessionModal() {
  const isVidyoConnected = useVidyoStore((store) => store.isVidyoConnected);
  const [
    isReturnToParentSessionModalOpen,
    setIsReturnToParentSessionModalOpen,
  ] = useState(false);
  const [countdown, setCountdown] = useState(15);
  const navigate = useNavigate();

  const shouldReturnToParentSession = useVidyoStore(
    (store) => store.breakoutSession.shouldReturnToParentSession,
  );
  const redirectReason = useVidyoStore(
    (store) => store.breakoutSession.redirectReason,
  );

  const parentSessionRoomKey = useVidyoStore(
    (store) => store.breakoutSession.parentSession?.roomKey,
  );
  const muteMicrophone = useVidyoStore((state) => state.microphone.mute);

  const shouldReturnImmediately =
    redirectReason === "kick-from-breakout-session" ||
    redirectReason === "breakout-session-timeout";
  /**
   * Open modal, when closeBreakoutSession is called
   * We don't want to show the modal if the user was kicked from the breakout session,
   * but redirect immediately to the parent session.
   */
  useEffect(() => {
    if (
      isVidyoConnected &&
      shouldReturnToParentSession &&
      !shouldReturnImmediately
    ) {
      setIsReturnToParentSessionModalOpen(true);
    }
  }, [isVidyoConnected, shouldReturnToParentSession, shouldReturnImmediately]);

  const handleReturnToParentSession = useCallback(async () => {
    if (!parentSessionRoomKey) {
      return;
    }

    setIsReturnToParentSessionModalOpen(false);
    muteMicrophone();

    navigate(`/r/${parentSessionRoomKey}/call`, { replace: true });
  }, [navigate, parentSessionRoomKey, muteMicrophone]);

  /**
   * If the user was kicked from the breakout session,
   * we don't want to show the modal,
   * but redirect immediately to the parent session.
   */
  useEffect(() => {
    if (shouldReturnImmediately && shouldReturnToParentSession) {
      handleReturnToParentSession();
    }
  }, [
    shouldReturnImmediately,
    handleReturnToParentSession,
    shouldReturnToParentSession,
  ]);

  useEffect(() => {
    if (
      !isReturnToParentSessionModalOpen ||
      !isVidyoConnected ||
      shouldReturnImmediately
    ) {
      setCountdown(15);
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleReturnToParentSession();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [
    isReturnToParentSessionModalOpen,
    handleReturnToParentSession,
    isVidyoConnected,
    shouldReturnImmediately,
  ]);

  return (
    <AlertDialog
      open={isReturnToParentSessionModalOpen}
      onOpenChange={setIsReturnToParentSessionModalOpen}
    >
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>Return to Parent Session?</AlertDialogTitle>
          <AlertDialogDescription>
            You are about to be returned to the parent session. This will end
            your current breakout session.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter className="sm:justify-end">
          <Button
            isAnimated
            onClick={handleReturnToParentSession}
            className="min-w-[135px]" // prevent layout shift
            variant="default"
          >
            Return Now{" "}
            <span className="ml-1 font-normal text-foreground/40">
              ({countdown}s)
            </span>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
